import React from 'react';
import useApi, {CONTROLLER} from "../../hooks/useApi";
import {StoreContext} from "../../store";
import {migrateDataSearchPreference} from './dataSearchPreference';
import {
  normalizeSortInfo,
  reduceSortInfoOnHeaderClick,
  shouldIgnoreHeaderSortClick,
  DESC_SORT_DIR,
} from './sortState';

const resolveSortInfo = (storedSortInfo, defaultSortInfo) => {
  const stored = normalizeSortInfo(storedSortInfo);
  if (stored && stored.length) {
    return stored;
  }
  return normalizeSortInfo(defaultSortInfo);
};

const normalizeFilterValue = (storedFilterValue, defaultFilterValue) => {
  if (!Array.isArray(defaultFilterValue)) {
    return storedFilterValue;
  }
  if (!Array.isArray(storedFilterValue)) {
    return defaultFilterValue;
  }

  const defaultsByName = new Map(
    defaultFilterValue
      .filter((item) => item && item.name)
      .map((item) => [item.name, item]),
  );

  const merged = storedFilterValue
    .filter((item) => item && item.name && defaultsByName.has(item.name))
    .map((item) => ({...defaultsByName.get(item.name), ...item}));

  const existing = new Set(merged.map((item) => item.name));
  defaultFilterValue.forEach((item) => {
    if (!item || !item.name || existing.has(item.name)) {
      return;
    }
    merged.push(item);
  });

  return merged;
};

const useStorage = (ref, name, props) => {
  const {loading, execute} = useApi(CONTROLLER.PREF_SAVE, {urlParams: {name}});

  const {store: {preferences}} = React.useContext(StoreContext);
  const migrated = React.useMemo(() => {
    const preference = (preferences || {})[name] || {};
    return name === 'dataSearch' ? migrateDataSearchPreference(preference) : {preference};
  }, [preferences, name]);
  const pref = migrated.preference;
  const [filterConflict, setFilterConflict] = React.useState(migrated.filterConflict);
  React.useEffect(() => setFilterConflict(migrated.filterConflict), [migrated]);

  const resolvedSortInfo = React.useMemo(
    () => resolveSortInfo(pref?.sortInfo, props.defaultSortInfo),
    [pref?.sortInfo, props.defaultSortInfo],
  );
  const resolvedFilterValue = React.useMemo(
    () => normalizeFilterValue(pref?.filterValue, props.defaultFilterValue),
    [pref?.filterValue, props.defaultFilterValue],
  );
  const preferenceSignature = React.useMemo(
    () => JSON.stringify({
      columns: pref?.columns || null,
      sortInfo: resolvedSortInfo || null,
      columnOrder: pref?.columnOrder || null,
      filterValue: resolvedFilterValue || null,
    }),
    [pref?.columns, pref?.columnOrder, resolvedSortInfo, resolvedFilterValue],
  );
  const columnPreferenceSignature = React.useMemo(
    () => JSON.stringify({
      columns: pref?.columns || null,
      columnOrder: pref?.columnOrder || null,
    }),
    [pref?.columns, pref?.columnOrder],
  );

  const [columns, setColumns] = React.useState(pref?.columns);
  const [sortInfo, setSortInfo] = React.useState(resolvedSortInfo);
  const [columnOrder, setColumnOrder] = React.useState(pref?.columnOrder);
  const [filterValue, setFilterValue] = React.useState(resolvedFilterValue);

  React.useEffect(() => {
    setColumns(pref?.columns);
    setSortInfo(resolvedSortInfo);
    setColumnOrder(pref?.columnOrder);
    setFilterValue(resolvedFilterValue);
  }, [preferenceSignature, pref?.columns, pref?.columnOrder, resolvedSortInfo, resolvedFilterValue]);

  const _columns = React.useMemo(
    () => (props.columns || []).map((item) => {
      const persisted = (columns || []).find((column) => column.name === item.name) || {};
      const merged = {
        ...item,
        ...persisted,
      };

      const shouldAttachManualSort = Boolean(props.manualSortOnHeader && merged.sortable !== false && merged.name);
      if (!shouldAttachManualSort) {
        return merged;
      }

      const headerDOMProps = merged.headerDOMProps || {};
      const originalOnClick = headerDOMProps.onClick;
      const originalPreventSortOnClick = merged.preventSortOnClick;
      const defaultDir = merged.defaultSortingDirection === 'desc' ? DESC_SORT_DIR : undefined;

      return {
        ...merged,
        preventSortOnClick: (event, headerProps) => {
          if (typeof originalPreventSortOnClick === 'function' && originalPreventSortOnClick(event, headerProps) === true) {
            return true;
          }
          return true;
        },
        headerDOMProps: {
          ...headerDOMProps,
          onClick: (event, headerProps) => {
            if (typeof originalOnClick === 'function') {
              originalOnClick(event, headerProps);
            }

            if (shouldIgnoreHeaderSortClick(event)) {
              return;
            }

            setSortInfo((currentSortInfo) => reduceSortInfoOnHeaderClick(
              currentSortInfo,
              {
                columnName: merged.name,
                shiftKey: Boolean(event?.shiftKey),
                defaultDir,
              },
            ));
          },
        },
      };
    }),
    [props.columns, columns, props.manualSortOnHeader],
  );

  const onColumnVisibleChange = React.useCallback(({column, visible}) => setColumns(
    (columns) => [
      ...(columns || [])?.filter((item) => item.name !== column.name),
      {...(columns || [])?.find((item) => item.name === column.name), name: column.name, visible},
    ]
  ), []);

  const onColumnResize = React.useCallback(({column, flex, width}) => setColumns(
    (columns) => [
      ...(columns || [])?.filter((item) => item.name !== column.name),
      {...(columns || [])?.find((item) => item.name === column.name), name: column.name, flex, width},
    ]
  ), []);

  const onSortInfoChange = React.useCallback((nextSortInfo) => {
    if (props.manualSortOnHeader) {
      return;
    }
    setSortInfo(normalizeSortInfo(nextSortInfo));
  }, [props.manualSortOnHeader]);

  const onFilterValueChange = React.useCallback((nextFilterValue) => {
    const currentDate = filterValue?.find((item) => item.name === 'Date');
    const nextDate = nextFilterValue?.find((item) => item.name === 'Date');
    if (JSON.stringify(currentDate) !== JSON.stringify(nextDate) || nextDate?.value == null) {
      setFilterConflict(null);
    }
    setFilterValue(normalizeFilterValue(nextFilterValue, props.defaultFilterValue));
  }, [props.defaultFilterValue, filterValue]);

  React.useImperativeHandle(
    ref,
    () => ({
      validate() { return !filterConflict; },
      save() {
        if (filterConflict) return;
        execute({
          columns: columns?.map(column =>
            ['name', 'visible', 'width', 'flex'].reduce(
              (previousValue, currentValue) => ({...previousValue, [currentValue]: column[currentValue]}),
              {},
            )
          ),
          sortInfo,
          columnOrder,
          filterValue,
        });
      },
      reset() {
        execute({});
      },
    }),
  );

  return {
    loading,
    sortInfo, onSortInfoChange,
    columnOrder, onColumnOrderChange: setColumnOrder,
    columns: _columns,
    onColumnResize,
    onColumnVisibleChange,
    filterValue, onFilterValueChange,
    initialSortInfo: resolvedSortInfo,
    initialFilterValue: resolvedFilterValue,
    preferenceSignature,
    columnPreferenceSignature,
    filterConflict,
  };
};

export default useStorage;
